// src/components/settings/shared/DesignSection.jsx
import React from 'react';
import { Palette, Upload, ImageIcon, Trash2 } from 'lucide-react';
import { FormField, TextInput } from './FormComponents';

export const DesignBrandingSection = ({ 
  formData, 
  onInputChange, 
  onImageUpload,
  uploadStates = {},
  errors = {} 
}) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Design & Branding</h3>
        <p className="text-gray-600">
          Customize the visual appearance and branding of your institution
        </p>
      </div>

      {/* Color Scheme */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Color Scheme</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FormField label="Primary Color" description="Main theme color">
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.primaryColor || '#6366f1'}
                onChange={(e) => onInputChange('primaryColor', e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData.primaryColor || '#6366f1'}
                onChange={(e) => onInputChange('primaryColor', e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#6366f1"
              />
            </div>
          </FormField>

          <FormField label="Accent Color" description="Accent color for highlights">
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.accentColor || '#8b5cf6'}
                onChange={(e) => onInputChange('accentColor', e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData.accentColor || '#8b5cf6'}
                onChange={(e) => onInputChange('accentColor', e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#8b5cf6"
              />
            </div>
          </FormField>

          <FormField label="Background Color" description="Page background">
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.backgroundColor || '#f8fafc'}
                onChange={(e) => onInputChange('backgroundColor', e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData.backgroundColor || '#f8fafc'}
                onChange={(e) => onInputChange('backgroundColor', e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#f8fafc"
              />
            </div>
          </FormField>

          <FormField label="Text Color" description="Main text color">
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.textColor || '#1f2937'}
                onChange={(e) => onInputChange('textColor', e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData.textColor || '#1f2937'}
                onChange={(e) => onInputChange('textColor', e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#1f2937"
              />
            </div>
          </FormField>

          <FormField label="Header Background" description="Header section background">
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.headerColor || '#ffffff'}
                onChange={(e) => onInputChange('headerColor', e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData.headerColor || '#ffffff'}
                onChange={(e) => onInputChange('headerColor', e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#ffffff"
              />
            </div>
          </FormField>

          <FormField label="Header Text Color" description="Text color in header">
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.headerTextColor || '#1f2937'}
                onChange={(e) => onInputChange('headerTextColor', e.target.value)}
                className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={formData.headerTextColor || '#1f2937'}
                onChange={(e) => onInputChange('headerTextColor', e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#1f2937"
              />
            </div>
          </FormField>
        </div>
      </div>

      <FormField label="Logo Invert" description="Invert logo color (0-100%)">
        <div className="space-y-2">
          <input
            type="range"
            min="0"
            max="100"
            value={formData.logoInvert || 0}
            onChange={(e) => onInputChange('logoInvert', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Normal</span>
            <span>{formData.logoInvert || 0}%</span>
            <span>Inverted</span>
          </div>
        </div>
      </FormField>

      <FormField label="Gallery Sidebar Background" description="Background color for gallery navigation">
        <div className="flex gap-2">
          <input
            type="color"
            value={formData.gallerySidebarBg || '#ffffff'}
            onChange={(e) => onInputChange('gallerySidebarBg', e.target.value)}
            className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
          />
          <input
            type="text"
            value={formData.gallerySidebarBg || '#ffffff'}
            onChange={(e) => onInputChange('gallerySidebarBg', e.target.value)}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="#ffffff"
          />
        </div>
      </FormField>

      <FormField label="Gallery Sidebar Shadow" description="Shadow color for gallery navigation">
        <div className="flex gap-2">
          <input
            type="color"
            value={formData.gallerySidebarShadow || '#000000'}
            onChange={(e) => onInputChange('gallerySidebarShadow', e.target.value)}
            className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
          />
          <input
            type="text"
            value={formData.gallerySidebarShadow || '#000000'}
            onChange={(e) => onInputChange('gallerySidebarShadow', e.target.value)}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="#000000"
          />
        </div>
      </FormField>

      <FormField label="Header Background Opacity" description="Adjust header transparency (0-100%)">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="100"
              value={formData.headerOpacity || 0.5}
              onChange={(e) => onInputChange('headerOpacity', parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Transparent</span>
                <span>{Math.round((formData.headerOpacity || 0.5) * 100)}%</span>
                <span>Opaque</span>
              </div>
          </div>
        </div>
      </FormField>

      {/* Header Background Image */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Header Background</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Header Background Image Upload */}
          <FormField label="Header Background Image" description="Optional background image for header">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              {formData.headerBackgroundUrl ? (
                <div className="relative">
                  <img
                    src={formData.headerBackgroundUrl}
                    alt="Header Background Preview"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => onInputChange('headerBackgroundUrl', '')}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file && onImageUpload) {
                          onImageUpload('headerBackgroundUrl', file);
                        }
                      }}
                      className="hidden"
                      id="header-bg-upload"
                    />
                    <label
                      htmlFor="header-bg-upload"
                      className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      {uploadStates.headerBackgroundUrl === 'uploading' ? 'Uploading...' : 'Upload Background'}
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: 1200x300px, JPG or PNG
                  </p>
                </div>
              )}
            </div>
            {uploadStates.headerBackgroundUrl === 'error' && (
              <p className="text-sm text-red-600 mt-1">Failed to upload image</p>
            )}
          </FormField>

          {/* Header Overlay Settings */}
          <div className="space-y-4">
            <FormField label="Header Overlay" description="Overlay color for better text readability">
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.headerOverlay || 'rgba(0, 0, 0, 0.3)'}
                  onChange={(e) => onInputChange('headerOverlay', e.target.value)}
                  className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.headerOverlay || 'rgba(0, 0, 0, 0.3)'}
                  onChange={(e) => onInputChange('headerOverlay', e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="rgba(0, 0, 0, 0.3)"
                />
              </div>
            </FormField>

            <FormField label="Header Overlay Opacity" description="Adjust overlay transparency">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={formData.headerOverlayOpacity || 0.3}
                onChange={(e) => onInputChange('headerOverlayOpacity', parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Transparent</span>
                <span>{Math.round((formData.headerOverlayOpacity || 0.3) * 100)}%</span>
                <span>Opaque</span>
              </div>
            </FormField>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Typography</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Font Family" description="Primary font for the institution">
            <select
              value={formData.fontFamily || 'Inter, sans-serif'}
              onChange={(e) => onInputChange('fontFamily', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
               <option value="Inter">Inter (Modern Sans-serif)</option>
                      <option value="Roboto">Roboto (Google Sans-serif)</option>
                      <option value="Open Sans">Open Sans (Friendly Sans-serif)</option>
                      <option value="Montserrat">Montserrat (Geometric Sans-serif)</option>
                      <option value="Poppins">Poppins (Rounded Sans-serif)</option>
                      <option value="Lato">Lato (Humanist Sans-serif)</option>
                      <option value="Nunito">Nunito (Rounded Sans-serif)</option>
                      <option value="Source Sans Pro">Source Sans Pro (Clean Sans-serif)</option>
                      <option value="Raleway">Raleway (Elegant Sans-serif)</option>
                      <option value="Ubuntu">Ubuntu (Modern Sans-serif)</option>
                      <option value="Playfair Display">Playfair Display (Elegant Serif)</option>
                      <option value="Merriweather">Merriweather (Reading Serif)</option>
                      <option value="Georgia">Georgia (Classic Serif)</option>
                      <option value="Times New Roman">Times New Roman (Traditional Serif)</option>
                      <option value="Crimson Text">Crimson Text (Book Serif)</option>
            </select>
          </FormField>

          <FormField label="Border Radius" description="Rounded corners for elements">
            <select
              value={formData.borderRadius || '8px'}
              onChange={(e) => onInputChange('borderRadius', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="0px">Sharp (0px)</option>
              <option value="4px">Slightly Rounded (4px)</option>
              <option value="8px">Rounded (8px)</option>
              <option value="12px">More Rounded (12px)</option>
              <option value="16px">Very Rounded (16px)</option>
              <option value="24px">Pill-like (24px)</option>
            </select>
          </FormField>
        </div>
      </div>

      <div className="space-y-6 border-t pt-6">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Image Settings</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FormField label="Grid Columns" description="Number of columns in grid layout">
            <select
              value={formData.gridColumns || 3}
              onChange={(e) => onInputChange('gridColumns', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
              <option value="4">4 Columns</option>
              <option value="5">5 Columns</option>
            </select>
          </FormField>

          <FormField label="Image Fit" description="How images fill their containers">
            <select
              value={formData.imageFit || 'cover'}
              onChange={(e) => onInputChange('imageFit', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="cover">Cover (Fill)</option>
              <option value="contain">Contain (Fit)</option>
              <option value="fill">Fill (Stretch)</option>
              <option value="scale-down">Scale Down</option>
            </select>
          </FormField>

          <FormField label="Aspect Ratio" description="Image aspect ratio">
            <select
              value={formData.imageAspectRatio || '1:1'}
              onChange={(e) => onInputChange('imageAspectRatio', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="1:1">Square (1:1)</option>
              <option value="4:3">Standard (4:3)</option>
              <option value="16:9">Wide (16:9)</option>
              <option value="3:4">Portrait (3:4)</option>
              <option value="2:3">Tall (2:3)</option>
            </select>
          </FormField>
        </div>
      </div>

      {/* Preview Section */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Preview</h4>
        
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Header Preview */}
          <div 
            className="relative p-4 flex items-center justify-between min-h-[100px]"
            style={{ 
              backgroundColor: formData.headerColor,
              backgroundImage: formData.headerBackgroundUrl ? `url(${formData.headerBackgroundUrl})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            {/* Header Overlay */}
            {formData.headerBackgroundUrl && (
              <div 
                className="absolute inset-0"
                style={{ 
                  backgroundColor: formData.headerOverlay || 'rgba(0, 0, 0, 0.3)',
                  opacity: formData.headerOverlayOpacity || 0.3
                }}
              />
            )}
            
            {/* Header Content */}
            <div className="relative z-10 flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                {formData.logoUrl ? (
                    <img 
                      src={formData.logoUrl} 
                      alt="Logo" 
                      className="h-8 w-auto" 
                      style={{ filter: `invert(${formData.logoInvert || 0}%)` }}
                    />
                  ) : (
                  <div className="w-8 h-8 bg-gray-300 rounded flex items-center justify-center">
                    <span className="text-xs text-gray-600">LOGO</span>
                  </div>
                )}
                <h1 
                  className="text-lg font-semibold"
                  style={{ 
                    color: formData.headerTextColor,
                    fontFamily: formData.fontFamily
                  }}
                >
                  {formData.botName || 'Institution Name'}
                </h1>
              </div>
              
              <div className="flex gap-2">
                <div 
                  className="px-3 py-1 rounded text-sm"
                  style={{ 
                    backgroundColor: formData.primaryColor,
                    color: 'white',
                    borderRadius: formData.borderRadius
                  }}
                >
                  Enroll Now
                </div>
              </div>
            </div>
          </div>
          
          {/* Content Preview */}
          <div 
            className="p-4"
            style={{ 
              backgroundColor: formData.backgroundColor,
              fontFamily: formData.fontFamily
            }}
          >
            <h2 className="text-xl font-semibold mb-2" style={{ color: formData.textColor }}>
              Welcome to Our Institution
            </h2>
            <p className="text-gray-600 mb-4">
              This is how your content will appear with the selected design settings.
            </p>
            <button 
              className="px-4 py-2 text-white text-sm font-medium"
              style={{ 
                backgroundColor: formData.secondaryColor,
                borderRadius: formData.borderRadius
              }}
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};